import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { useAdmin } from "@/context/AdminContext";

export const useNav = () => {
  const { pages } = useAdmin();
  return pages.filter((p) => p.show_in_menu).map((p) => ({
    to: p.path,
    label: p.slug === "fan-mail" ? "Fan Mail Portal" : p.title,
    id: p.slug,
  }));
};

export const Brand = ({ testId, light = false }) => (
  <Link to="/" data-testid={testId} className="group flex items-center gap-3">
    <img src="/assets/logo.png" alt="Cresway Media logo" className="h-11 w-11 object-contain transition-transform duration-500 group-hover:-rotate-6 group-hover:scale-110" />
    <span className={`font-display text-lg sm:text-xl font-extrabold tracking-tight ${light ? "text-white" : "text-[#0A0A0A]"}`}>Cresway Media</span>
  </Link>
);

export const Header = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const nav = useNav();
  const { user, setLoginOpen, logout } = useAdmin();

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <>
      <header data-testid="site-header" className="fixed inset-x-0 top-0 z-[80] h-20 border-b border-black/10 bg-[#FBF9F5]">
        <div className="mx-auto flex h-full max-w-7xl items-center justify-between gap-3 px-4 sm:px-8 lg:px-12">
          <Brand testId="header-logo-link" />
          <div className="flex shrink-0 items-center gap-2 sm:gap-3">
            <button type="button" aria-haspopup="true" aria-expanded={open} aria-controls="site-menu-panel" data-testid="site-menu-toggle" onClick={() => setOpen((value) => !value)}
              className={`inline-flex h-10 items-center gap-2 border px-3 text-sm font-bold transition-colors sm:px-4 ${open ? "border-[#E62314] bg-[#0A0A0A] text-white" : "border-black/25 bg-transparent text-[#0A0A0A] hover:border-black"}`}>
              <span>Menu</span>
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {user && <Link to="/dashboard" data-testid="header-login-button-dashboard" className="hidden whitespace-nowrap border border-black/25 px-3 py-2 text-sm font-semibold hover:border-black lg:inline-flex">Dashboard</Link>}
            <button onClick={() => (user ? logout() : setLoginOpen(true))} data-testid="header-login-button"
              className="whitespace-nowrap border border-black/80 px-3 py-2 text-sm font-semibold transition-colors hover:bg-black hover:text-white sm:px-4">
              {user ? "Log out" : "Log in"}
            </button>
          </div>
        </div>
      </header>
      <AnimatePresence>
        {open && <>
          <motion.button type="button" aria-label="Close menu" onClick={() => setOpen(false)} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-x-0 bottom-0 top-20 z-[60] bg-black/35" data-testid="site-menu-backdrop" />
          <motion.nav id="site-menu-panel" aria-label="Site pages" data-lenis-prevent data-testid="site-menu-panel"
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.18 }}
            className="fixed inset-x-0 top-20 z-[70] max-h-[calc(100dvh-5rem)] overflow-y-auto border-y border-black/10 bg-[#FBF9F5] shadow-2xl">
            <div className="mx-auto grid max-w-7xl gap-0 px-4 py-4 sm:grid-cols-2 sm:px-8 sm:py-6 lg:grid-cols-3 lg:px-12">
              {nav.map((page, index) => <NavLink key={page.to} to={page.to} end onClick={() => setOpen(false)} data-testid={`menu-nav-${page.id || page.label.toLowerCase()}-link`}
                className={({ isActive }) => `flex min-h-14 items-center justify-between border-b border-black/10 px-3 py-4 text-sm font-bold transition-colors hover:bg-black/[0.035] ${isActive ? "text-[#B51F13]" : "text-[#0A0A0A]"}`}>
                <span className="mr-3 font-mono text-[10px] text-[#8A8580]">{String(index + 1).padStart(2, "0")}</span><span className="flex-1">{page.label}</span>
              </NavLink>)}
              {user && <NavLink to="/dashboard" end onClick={() => setOpen(false)} data-testid="menu-dashboard-link"
                className={({ isActive }) => `flex min-h-14 items-center justify-between border-b border-black/10 px-3 py-4 text-sm font-bold transition-colors hover:bg-black/[0.035] lg:hidden ${isActive ? "text-[#B51F13]" : "text-[#0A0A0A]"}`}>
                <span className="mr-3 font-mono text-[10px] text-[#8A8580]">{String(nav.length + 1).padStart(2, "0")}</span><span className="flex-1">Dashboard</span>
              </NavLink>}
            </div>
          </motion.nav>
        </>}
      </AnimatePresence>
    </>
  );
};
